/**
 * Chat program hook - effect runner.
 *
 * Elm-ish architecture: This is the ONLY place that combines:
 * - useState/useReducer for state management
 * - dispatch for message handling
 * - update() for pure state transitions
 * - runEffect() for side effects (outside the reducer)
 *
 * The reducer is PURE - it only returns new state.
 * Effects are queued in state and executed via useEffect.
 *
 * This hook bridges the pure Elm model to React's rendering.
 *
 * ACT-INDEEP-SPONSOR-FRIEND-PERSONAL-CHAT-REALTIME01:
 * The realtime socket lifecycle is owned by this hook directly
 * (NOT via the reducer's pendingEffects queue). This prevents
 * opening the socket from clobbering in-flight HTTP-driven
 * effects like LoadMessages. The hook dispatches
 * RealtimeStatusChanged / RealtimeMessageReceived events to the
 * reducer so the view can observe realtime state.
 */
import { useReducer, useEffect, useRef, useCallback } from 'react';
import { initialModel, type ChatModel } from './model';
import { type ChatMsg } from './messages';
import { update, type ChatEffect } from './update';
import { runEffect, type EffectContext } from './effects';
import { ChatRealtimeClient } from './realtime/chatRealtime';

/**
 * Result of the program hook - exposes model and action callbacks.
 */
export interface UseChatProgramResult {
  readonly model: ChatModel;
  readonly start: () => void;
  readonly openChannel: (channelId: string) => void;
  readonly backToList: () => void;
  readonly showCreateForm: () => void;
  readonly hideCreateForm: () => void;
  readonly createChannel: () => void;
  readonly sendMessage: () => void;
  readonly newMessageChanged: (text: string) => void;
  readonly retry: () => void;
}

type ProgramState = {
  readonly model: ChatModel;
  readonly pendingEffects: readonly ChatEffect[];
};

const initialState: ProgramState = {
  model: initialModel,
  pendingEffects: [],
};

function createEffectContext(dispatch: (msg: ChatMsg) => void): EffectContext {
  return { dispatch };
}

export function useChatProgram(): UseChatProgramResult {
  const dispatchRef = useRef<((msg: ChatMsg) => void)>(() => {});

  const [state, baseDispatch] = useReducer(
    (currentState: ProgramState, msg: ChatMsg): ProgramState => {
      const result = update(currentState.model, msg);
      return {
        model: result.model,
        pendingEffects: result.effects,
      };
    },
    initialState,
  );

  dispatchRef.current = baseDispatch;

  useEffect(() => {
    if (state.pendingEffects.length === 0) return;

    const context = createEffectContext(baseDispatch);

    for (const effect of state.pendingEffects) {
      void runEffect(effect, context);
    }
  }, [state.pendingEffects, baseDispatch]);

  // ACT-INDEEP-SPONSOR-FRIEND-PERSONAL-CHAT-REALTIME01: own the
  // realtime socket lifecycle inside the hook. The hook
  // dispatches RealtimeStatusChanged / RealtimeMessageReceived
  // events to the reducer on every observable socket event. The
  // client is opened on mount and closed on unmount.
  useEffect(() => {
    const client = new ChatRealtimeClient({
      onMessage: (message) => {
        baseDispatch({
          type: 'RealtimeMessageReceived',
          message: {
            id: message.id,
            channelId: message.channelId,
            senderAccountId: message.senderAccountId,
            content: message.content,
            createdAt: message.createdAt,
          },
        });
      },
      onConnectionReady: () => {
        // No-op; onStatusChange handles the connected transition.
      },
      onStatusChange: (status) => {
        baseDispatch({ type: 'RealtimeStatusChanged', status });
      },
      onError: (_err) => {
        // Errors are observable through the status badge; we
        // intentionally do not propagate to the error state.
      },
    });
    client.connect();
    return () => {
      client.close();
    };
  }, [baseDispatch]);

  const start = useCallback(() => {
    baseDispatch({ type: 'Started' });
  }, []);

  const openChannel = useCallback((channelId: string) => {
    baseDispatch({ type: 'OpenChannel', channelId });
  }, []);

  const backToList = useCallback(() => {
    baseDispatch({ type: 'BackToList' });
  }, []);

  const showCreateForm = useCallback(() => {
    baseDispatch({ type: 'ShowCreateForm' });
  }, []);

  const hideCreateForm = useCallback(() => {
    baseDispatch({ type: 'HideCreateForm' });
  }, []);

  const createChannel = useCallback(() => {
    baseDispatch({ type: 'CreateChannel' });
  }, []);

  const sendMessage = useCallback(() => {
    baseDispatch({ type: 'SendMessage' });
  }, []);

  const newMessageChanged = useCallback((text: string) => {
    baseDispatch({ type: 'NewMessageChanged', text });
  }, []);

  const retry = useCallback(() => {
    baseDispatch({ type: 'Retry' });
  }, []);

  return {
    model: state.model,
    start,
    openChannel,
    backToList,
    showCreateForm,
    hideCreateForm,
    createChannel,
    sendMessage,
    newMessageChanged,
    retry,
  };
}
